import React from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import styled, { ThemeProvider } from 'styled-components';
import postal from 'postal';

import theme from '../../../../../assets/stylesheets/_settings.global.scss';
import TextArea from '../../../shared/text-area';
import Loading from '../../../../shared/loading';
import { useDeleteCTA, useUpdateCTA, useGetPresetCTA } from '../../../../services/wire/cta-service';
import useEditorStore from '../state-hook';
import {
  Button, BottomLink, Container, Link, Title, TitleContainer,
} from './shared/styles';
import { selectorProps } from '../../../lwadmin/common/form';

const MContainer = styled(Container)`
  margin-bottom: 24px;
`;

const TextTitle = styled.h2`
  font-size: 24px;
  font-weight: ${props => props.theme.fontWeightHeavy};
  margin: 12px 0;
`;

const TextMessage = styled.p`
  font-size: 19px;
  margin: 9px 0;
`;

const Field = styled.div`
  label {
    font-weight: ${props => props.theme.fontWeightBold};
  }

  textarea {
    resize: none;
    padding: 8px 16px;
    font-size: 19px;
    font-weight: ${props => props.theme.fontWeightRegular};
  }
`;

const FieldPresetList = styled(Field)`
  label {
    font-size: 14px;
    text-transform: uppercase;
  }

  & > div {
    margin-bottom: 24px;
  }

  .preset-cta-option {
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 8px;
    padding-left: 8px;
  }
`;

const FieldTitle = styled(Field)`
  textarea {
    font-size: 24px;
    font-weight: ${props => props.theme.fontWeightHeavy};
  }
`;

const FieldText = styled(Field)`
  textarea {
    color: ${props => props.theme.primaryColor};
    text-decoration: underline;

    &::placeholder {
      color: ${props => props.theme.secondaryColor};
      opacity: 0.5;
    }
  }
`;

const FieldNamesMap = {
  title: 'Title',
  message: 'Message',
  text: 'Call to Action Text',
  url: 'Call to Action Link',
};

const buildErrorMessage = (fieldName, errors) => {
  const fieldErrors = errors[fieldName] || [];

  return fieldErrors.length > 0 ? `${FieldNamesMap[fieldName]} ${fieldErrors[0]}` : '';
};

const customSelectorProps = {
  ...selectorProps,
  styles: {
    ...selectorProps.styles,
    option: (provided, state) => ({
      ...provided,
      // eslint-disable-next-line no-nested-ternary
      backgroundColor: state.isSelected ? '#FBF5E5' : (state.isFocused ? '#FBF5E5' : '#fff'),
      // eslint-disable-next-line no-nested-ternary
      color: state.isSelected ? '#B88E1E' : (state.isFocused ? '#B88E1E' : '#19202d'),
      fontSize: 18,
      fontWeight: 400,
      textTransform: 'none',
      padding: '3px 12px',
    }),
    groupHeading: provided => ({
      ...provided,
      marginTop: '4px',
      marginBottom: 0,
    }),
    group: provided => ({
      ...provided,
      paddingTop: '4px',
      paddingBottom: 0,
    }),
  },
};

const PresetCTAOption = props => (
  <components.Option {...props}>
    <div className="preset-cta-option">
      {props.data.type === 'contributor' && (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="user-01">
            <path
              id="Icon"
              d="M13.3334 14C13.3334 13.0696 13.3334 12.6044 13.2185 12.2259C12.96 11.3736 12.293 10.7067 11.4408 10.4482C11.0623 10.3333 10.5971 10.3333 9.66668 10.3333H6.33336C5.40298 10.3333 4.93779 10.3333 4.55926 10.4482C3.70699 10.7067 3.04005 11.3736 2.78151 12.2259C2.66669 12.6044 2.66669 13.0696 2.66669 14M11 5C11 6.65685 9.65687 8 8.00002 8C6.34317 8 5.00002 6.65685 5.00002 5C5.00002 3.34315 6.34317 2 8.00002 2C9.65687 2 11 3.34315 11 5Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </svg>
      )}
      {props.data.type === 'company' && (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="building-09" clipPath="url(#clip0_554_399)">
            <g id="line">
              <path
                d="M12 10.0001C12.3682 10.0001 12.6667 9.7016 12.6667 9.33341C12.6667 8.96522 12.3682 8.66675 12 8.66675C11.6318 8.66675 11.3334 8.96522 11.3334 9.33341C11.3334 9.7016 11.6318 10.0001 12 10.0001Z"
                fill="currentColor"
              />
              <path
                d="M12.6667 12.0001C12.6667 12.3683 12.3682 12.6667 12 12.6667C11.6318 12.6667 11.3334 12.3683 11.3334 12.0001C11.3334 11.6319 11.6318 11.3334 12 11.3334C12.3682 11.3334 12.6667 11.6319 12.6667 12.0001Z"
                fill="currentColor"
              />
              <path
                d="M4.00002 7.33341C4.36821 7.33341 4.66669 7.03494 4.66669 6.66675C4.66669 6.29856 4.36821 6.00008 4.00002 6.00008C3.63183 6.00008 3.33335 6.29856 3.33335 6.66675C3.33335 7.03494 3.63183 7.33341 4.00002 7.33341Z"
                fill="currentColor"
              />
              <path
                d="M4.66669 9.33341C4.66669 9.7016 4.36821 10.0001 4.00002 10.0001C3.63183 10.0001 3.33335 9.7016 3.33335 9.33341C3.33335 8.96522 3.63183 8.66675 4.00002 8.66675C4.36821 8.66675 4.66669 8.96522 4.66669 9.33341Z"
                fill="currentColor"
              />
              <path
                d="M4.00002 4.66675C4.36821 4.66675 4.66669 4.36827 4.66669 4.00008C4.66669 3.63189 4.36821 3.33341 4.00002 3.33341C3.63183 3.33341 3.33335 3.63189 3.33335 4.00008C3.33335 4.36827 3.63183 4.66675 4.00002 4.66675Z"
                fill="currentColor"
              />
              <path
                d="M7.33335 6.66675C7.33335 7.03494 7.03488 7.33341 6.66669 7.33341C6.2985 7.33341 6.00002 7.03494 6.00002 6.66675C6.00002 6.29856 6.2985 6.00008 6.66669 6.00008C7.03488 6.00008 7.33335 6.29856 7.33335 6.66675Z"
                fill="currentColor"
              />
              <path
                d="M6.66669 10.0001C7.03488 10.0001 7.33335 9.7016 7.33335 9.33341C7.33335 8.96522 7.03488 8.66675 6.66669 8.66675C6.2985 8.66675 6.00002 8.96522 6.00002 9.33341C6.00002 9.7016 6.2985 10.0001 6.66669 10.0001Z"
                fill="currentColor"
              />
              <path fillRule="evenodd" clipRule="evenodd" d="M0.666687 2.66675C0.666687 1.56218 1.56212 0.666748 2.66669 0.666748H8.00002C9.10459 0.666748 10 1.56218 10 2.66675V6.00008H13.3334C14.4379 6.00008 15.3334 6.89551 15.3334 8.00008V14.6667C15.3334 15.0349 15.0349 15.3334 14.6667 15.3334H1.33335C0.965164 15.3334 0.666687 15.0349 0.666687 14.6667V2.66675ZM10 14.0001V7.33341H13.3334C13.7015 7.33341 14 7.63189 14 8.00008V14.0001H10ZM2.66669 2.00008C2.2985 2.00008 2.00002 2.29856 2.00002 2.66675V14.0001H8.66669V2.66675C8.66669 2.29856 8.36821 2.00008 8.00002 2.00008H2.66669Z" fill="currentColor" />
            </g>
          </g>
          <defs>
            <clipPath id="clip0_554_399">
              <rect width="16" height="16" fill="white" />
            </clipPath>
          </defs>
        </svg>

      )}
      <span>{props.data.label}</span>
    </div>
  </components.Option>
);

PresetCTAOption.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string,
  }).isRequired,
};

export default function CTA() {
  const [editable, setEditable] = React.useState(false);
  const [presetCTAOptions, setPresetCTAOptions] = React.useState([]);
  const [selectedPresetCTA, setSelectedPresetCTA] = React.useState(null);
  const [errors, setErrors] = React.useState({});
  const [{ data, loading }, updateCTA] = useUpdateCTA();
  const [, deleteCTA] = useDeleteCTA();
  const [, getPresetCTA] = useGetPresetCTA();
  const set = useEditorStore(state => state.set);
  const wire = useEditorStore(state => state.wire);
  const cta = useEditorStore(state => state.cta);
  const presetCTA = useEditorStore(state => state.presetCTA);
  const loadingRef = React.useRef();

  const getPresetCTAList = () => {
    getPresetCTA(wire.id).then(res => {
      set(state => {
        state.presetCTA = res.data;
      });

      const contributorOptions = res.data.contributorCta.map(
        contributorCTA => ({
          type: 'contributor',
          value: contributorCTA,
          label: `${contributorCTA.contributor}, ${contributorCTA.company} - ${contributorCTA.title}`,
        }),
      );

      const companyOptions = res.data.companyCta.map(
        companyCTA => ({
          type: 'company',
          value: companyCTA,
          label: `${companyCTA.company} - ${companyCTA.title}`,
        }),
      );

      const groupedOptions = [];
      if (contributorOptions.length > 0) groupedOptions.push({ label: 'Contributor', options: contributorOptions });
      if (companyOptions.length > 0) groupedOptions.push({ label: 'Company', options: companyOptions });

      setPresetCTAOptions(groupedOptions);
    });
  };

  React.useEffect(() => {
    loadingRef.current && loadingRef.current.toggle(loading);
  }, [loading]);

  React.useEffect(() => {
    const editorSubscription = postal.subscribe({
      channel: 'editor',
      topic: 'wire-body-saved',
      callback: getPresetCTAList,
    });

    getPresetCTAList();

    return () => {
      editorSubscription.unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    if (!cta.id && data && data.id) {
      set(state => {
        state.cta.id = data.id;
      });
    }

    setErrors(data && data.errors ? data.errors : []);
  }, [data]);

  const toggleEditable = e => {
    e.preventDefault();
    setEditable(!editable);
  };

  const removeCTA = e => {
    e.preventDefault();
    deleteCTA(wire.id);
    setEditable(false);
    setErrors({});
    set(state => {
      state.cta = {};
    });
  };

  const onChange = (field, value) => {
    updateCTA(wire.id, { [field]: value });

    set(state => {
      state.cta[field] = value;
    });

    if (selectedPresetCTA) {
      setSelectedPresetCTA(null);
    }
  };

  let actionLabel;
  if (editable) {
    actionLabel = 'Done';
  } else {
    actionLabel = cta.id ? 'Edit CTA' : 'Add CTA';
  }

  const onPresetCTAChange = selected => {
    if (selected) {
      const { value } = selected;
      const {
        title,
        message,
        text,
        url,
      } = value;

      updateCTA(wire.id, {
        title,
        message,
        text,
        url,
      });

      set(state => {
        state.cta = {
          id: state.cta.id,
          title,
          message,
          text,
          url,
        };
      });

      setSelectedPresetCTA(selected);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <MContainer>
        <TitleContainer>
          <Title>Call to Action</Title>
          <Link onClick={toggleEditable}>{actionLabel}</Link>
          <Loading ref={loadingRef} variant="inline" />
          <Button className="fa fa-trash-o" onClick={removeCTA} />
        </TitleContainer>
        { !editable && cta.id && (
          <>
            <TextTitle>{cta.title}</TextTitle>
            <TextMessage>{cta.message}</TextMessage>
            <TextMessage><a href={cta.url}>{cta.text}</a></TextMessage>
          </>
        )}
        { editable && (
          <>
            {(presetCTA.companyCta.length > 0 || presetCTA.contributorCta.length > 0) && (
              <FieldPresetList>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="preset-list">
                  Preset List
                </label>
                <Select
                  inputId="preset-list"
                  aria-labelledby="preset-list"
                  {...customSelectorProps}
                  isMulti={false}
                  isDisabled={false}
                  options={presetCTAOptions}
                  components={{ Option: PresetCTAOption }}
                  onChange={onPresetCTAChange}
                  value={selectedPresetCTA}
                  placeholder="Choose CTA"
                />
              </FieldPresetList>
            )}
            <FieldTitle>
              <TextArea
                value={cta.title}
                label="Title"
                placeholder="Add concise, engaging title"
                height={44}
                limit={100}
                isLoading={false}
                error={buildErrorMessage('title', errors)}
                onChange={v => onChange('title', v)}
              />
            </FieldTitle>
            <Field>
              <TextArea
                value={cta.message}
                label="Message"
                placeholder="Add concise message to encourage readers to engage"
                height={110}
                limit={300}
                isLoading={false}
                error={buildErrorMessage('message', errors)}
                onChange={v => onChange('message', v)}
              />
            </Field>
            <FieldText>
              <TextArea
                value={cta.text}
                label="Call to Action Text"
                placeholder="Add succinct Call To Action text"
                height={44}
                limit={50}
                isLoading={false}
                error={buildErrorMessage('text', errors)}
                onChange={v => onChange('text', v)}
              />
            </FieldText>
            <Field>
              <TextArea
                value={cta.url}
                label="Call to Action Link"
                placeholder="Add website URL"
                height={44}
                limit={null}
                isLoading={false}
                error={buildErrorMessage('url', errors)}
                onChange={v => onChange('url', v)}
              />
            </Field>
            <BottomLink onClick={toggleEditable}>{actionLabel}</BottomLink>
          </>
        )}
      </MContainer>
    </ThemeProvider>
  );
}
